import React, {
    Component,
    MouseEvent
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';

import {confirmPopup} from 'primereact/confirmpopup';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

import {__} from '~/shared/src/utilities/messageCatalog';
import Item from '~/shared/src/ci/Item';
import User from '~/shared/src/model/User';

import {StoreState} from '~/frontend/src/store';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {ComponentProps as DetailProps} from '~/frontend/src/modules/panels/Finder/detail';

import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import * as FinderActions from '~/frontend/src/actions/panel/finderDetail';
import {setMessage} from '~/frontend/src/actions/intercom';
import {getXmlForContext} from '~/shared/src/utilities/xmlExport';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import parse from 'html-react-parser';

// this quirk is needed due to problems with circular dependencies
let saveData = require( '~/frontend/src/actions/panel/viewDesigner/saveData' ).default;

const Actions = Object.assign( { setMessage, saveData }, PanelActions, FinderActions ) as any;
type SetParam = <K extends keyof DetailProps>( panelKey : string, paramKey : K, paramValue : DetailProps[K] ) => SetParamAction;


const defaultProps = {
    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton: true as boolean,

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount: 0 as number,

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip: '' as string | JSX.Element | ( string | JSX.Element )[],

    /**
     * Should the clone Button shown in the toolbar <strong>or</strong> not.
     */
    showCloneButton: true as boolean,

    /**
     * Should the restore Button shown in the toolbar <strong>or</strong> not.
     */
    showRestoreButton: false as boolean,

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton: true as boolean,

    /**
     * Should the history Button shown in the toolbar <strong>or</strong> not.
     */
    showHistoryButton: true as boolean,

    /**
     * Show copy XML button.
     */
    showCopyXmlButton: true as boolean,

    /**
     * Show download XML button.
     */
    showDownloadXmlButton: true as boolean,

    /**
     * Should the AdvancedSearchOptionsMenu shown in the toolbar <strong>or</strong> not.
     */
    showAdvancedSearchOptions: true as boolean,

    /**
     * Should the view options button shown in the toolbar.
     */
    showViewOptionsButton: true as boolean,

    showPanelSelectorDropdown: true as boolean,

    /**
     * Show reload button.
     */
    showReloadButton: true as boolean

};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The id of the trees root item
     */
    rootItemId : number;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    dataContext : string;

    treeViewItem : Item;

    treeItemIds : number[];

    /**
     * Event for reload button
     */
    onReloadButtonClick? : () => void;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionProps = {
    // common panel actions
    setParam : SetParam;

    // finder actions
    saveData : typeof saveData;

    // intercom actions
    setMessage : typeof setMessage;
}

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {};


/**
 * Toolbar
 */
class Toolbar extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            item                : undefined,
            isItemUpdatable     : false,
            isItemDeletable     : false,
            isItemInsertable    : false,
            isItemTypeInsertable: false
        };

        this.onSaveButtonClick = this.onSaveButtonClick.bind( this );
        this.onCloneButtonClick = this.onCloneButtonClick.bind( this );
        this.onDeleteButtonClick = this.onDeleteButtonClick.bind( this );
        this.onDeleteConfirmButtonClick = this.onDeleteConfirmButtonClick.bind( this );
        this.onCopyXmlApply = this.onCopyXmlApply.bind( this );
        this.onDownloadXmlApply = this.onDownloadXmlApply.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
    }

    /**
     * Handler for Save button click.
     *
     * @private
     */
    onSaveButtonClick() {
        this.props.saveData( this.props.dataContext, this.props.schema, this.props.rootItemId );
    }

    /**
     * Handler for clone Button click.
     *
     * @private
     */
    onCloneButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        confirmPopup( {
            target         : event.currentTarget,
            message        : 'Not implemnted yet!',
            icon           : 'pi pi-exclamation-triangle yn-warning',
            acceptLabel    : __( 'YN.base.common.yes' ),
            acceptClassName: 'p-button p-button-outlined',
            rejectLabel    : __( 'YN.base.common.no' ),
            rejectClassName: 'p-button-secondary p-button-outlined mr-3'
        } );
    }


    /**
     * Handler for delete Button click.
     *
     * @private
     */
    onDeleteButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        confirmPopup( {
            target         : event.currentTarget,
            message        : __( 'YN.base.finder.detail.confirm.deleteItem' ),
            icon           : 'pi pi-info-circle yn-danger',
            acceptLabel    : __( 'YN.base.common.yes' ),
            acceptClassName: 'p-button p-button-outlined',
            rejectLabel    : __( 'YN.base.common.no' ),
            rejectClassName: 'p-button-secondary p-button-outlined mr-3',
            accept         : this.onDeleteConfirmButtonClick
        } );
    }

    /**
     * Handler for delete Button click.
     *
     * @private
     */
    onDeleteConfirmButtonClick() {
        if ( this.props.treeViewItem ) {
            /*this.props.item.reset();
            this.props.item.statusId = metaModel[ this.props.schema ].getStatus( 'deleted' )!.id;
            this.props.saveDetailData(
                this.props.uid,
                this.props.schema,
                [
                    this.props.uid,
                    'toast',
                    'deleteSuccess',
                    {
                        severity: 'success',
                        summary : __( 'YN.base.finder.save.deleteSuccess.title' ),
                        detail  : __( 'YN.base.finder.save.deleteSuccess.text' )
                    }
                ] );*/
        }
    }

    async onCopyXmlApply() {
        await navigator.clipboard.writeText( getXmlForContext( this.props.dataContext, this.props.treeItemIds, undefined, true, true ) );

        this.props.setMessage?.(
            this.props.uid,
            'toast',
            'xmlDataCopiedToClipboard',
            {
                severity: 'info',
                summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
            }
        );
    }

    onDownloadXmlApply() {
        const fileName = `ViewDesigner_${this.props.treeViewItem.key}`;
        fileDownload( getXmlForContext( this.props.dataContext, this.props.treeItemIds, undefined, true, true ), fileName, 'xml' );
    }

    /**
     * Handler for reload Button click.
     *
     * @private
     */
    onReloadButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        const {
                  dataContext,
                  schema,
                  rootItemId
              }          = this.props,
              reloadFnc  = () => {
                  if ( this.props.onReloadButtonClick ) {
                      this.props.onReloadButtonClick();
                  }
              },
              isModified = rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema! );

        if ( isModified ) {
            confirmPopup( {
                target         : event.currentTarget,
                message        : parse( `<span class="yn-warning yn-confirmpopup-title">${__( 'YN.base.panel.confirmReload.title' )}</span><br /><br />${__( 'YN.base.panel.confirmReload.text' )}` ),
                icon           : 'pi pi-exclamation-triangle yn-warning',
                acceptLabel    : __( 'YN.base.common.yes' ),
                acceptClassName: 'p-button p-button-outlined',
                rejectLabel    : __( 'YN.base.common.no' ),
                rejectClassName: 'p-button-secondary p-button-outlined mr-3',
                accept         : reloadFnc
            } ).show();
        }
        else {
            reloadFnc();
        }

    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  dataContext,
                  rootItemId,
                  showSaveButton,
                  saveButtonErrorCount,
                  saveButtonErrorTooltip,
                  showCloneButton,
                  showDeleteButton,
                  showCopyXmlButton,
                  showDownloadXmlButton,
                  showPanelSelectorDropdown,
                  showReloadButton
              }                                          = this.props,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        // left
        if ( showSaveButton ) {
            layoutComponents.left.push( {
                type  : 'saveButton',
                saveButtonErrorCount,
                saveButtonErrorTooltip,
                params: {
                    key: `${uid}_saveButton`,

                    icon     : 'pi pi-save',
                    onClick  : this.onSaveButtonClick,
                    className: 'p-button-text'
                }
            } );
        }

        if ( showCloneButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_cloneButton`,

                    icon     : 'pi pi-clone',
                    onClick  : this.onCloneButtonClick,
                    className: 'p-button-text'
                }
            } );
        }

        if ( showDeleteButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_deleteButton`,

                    icon     : 'pi pi-minus-circle',
                    onClick  : this.onDeleteButtonClick,
                    className: 'p-button-text p-button-danger p-button-rounded'
                }
            } );
        }

        layoutComponents.left.push( {
            type: 'separator'
        } );

        if ( showCopyXmlButton ) {
            layoutComponents.left.push( {
                type  : 'copyXml',
                params: {
                    key: `${uid}_copyXmlButton`,

                    uid,

                    onApply: () => {
                        void this.onCopyXmlApply();
                    }
                }
            } );
        }

        if ( showDownloadXmlButton ) {
            layoutComponents.left.push( {
                type  : 'downloadXml',
                params: {
                    key: `${uid}_downloadXmlButton`,

                    uid,

                    onApply: this.onDownloadXmlApply
                }
            } );
        }

        layoutComponents.left.push( {
            type: 'separator'
        } );

        // right
        if ( showPanelSelectorDropdown ) {
            const item       = rootItemId
                      ? global.data[ dataContext ]?.items[ rootItemId ]
                      : undefined,
                  itemParams = item
                      ? {
                          schema  : item.schema,
                          itemId  : item.id,
                          itemType: item.itemTypeId
                      }
                      : {};

            layoutComponents.right.push( {
                type  : 'panelSelectorDropdown',
                params: {
                    key: `${uid}_panelSelector`,

                    uid        : `${uid}_panelSelector`,
                    labeled    : true,
                    style      : { width: '18rem' },
                    currentView: `ViewDesigner`,
                    ...itemParams
                }
            } );
        }

        layoutComponents.right.push( {
            type: 'separator'
        } );

        if ( showReloadButton ) {
            layoutComponents.right.push( {
                type  : 'button',
                params: {
                    key: `${uid}_reloadButton`,

                    icon     : 'pi pi-refresh',
                    onClick  : this.onReloadButtonClick,
                    className: 'p-button-text'
                }
            } );
        }

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <>
            <PrimeToolbar
                left={toolbarComponents.left}
                right={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />
        </>;
    }
}

/**
 * REDUX stuff
 */
function

mapStateToProps( state : StoreState ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function

mapDispatchToProps( dispatch : Dispatch ) {
    // this quirk is needed due to problems with circular dependencies
    saveData = require( '~/frontend/src/actions/panel/treeView/saveData' ).default;
    Actions.saveData = saveData;
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( Toolbar );
